import { P } from '@/components/common/Typo'
import { Card, cn } from 'oddle-shop-monorepo.design-system'
import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

interface VerticalNavigationProps {
  items: Record<string, string>[]
  selectedItemId?: string
  onClickItem?: (event: MouseEvent<HTMLAnchorElement>, id: string) => void
  syncScroll?: boolean
  className?: string
}

const VerticalNavigation = ({
  items,
  selectedItemId,
  onClickItem = (_, id) => id,
  syncScroll,
  className,
}: VerticalNavigationProps): ReactNode => {
  const [prevSelectedItemId, setPrevSelectedItemId] = useState(selectedItemId)
  const refs = useMemo(() => new Map(), [])
  const containerRef = useRef<HTMLDivElement>(null)

  const handleScroll = useCallback(() => {
    const container = containerRef.current
    const targetNode = refs.get(selectedItemId)?.current

    if (targetNode && container) {
      if (
        container.scrollHeight > container.offsetHeight &&
        container.scrollTo
      ) {
        const top =
          targetNode.offsetTop - Math.trunc(container.offsetHeight / 2)
        container.scrollTo({ top, behavior: 'smooth' })
      }
    }
  }, [refs, selectedItemId])

  useEffect(() => {
    if (syncScroll && selectedItemId && prevSelectedItemId !== selectedItemId) {
      setPrevSelectedItemId(selectedItemId)
      handleScroll()
    }
  }, [selectedItemId, prevSelectedItemId, syncScroll, handleScroll])

  return (
    <Card
      className={cn(
        'bg-container sticky top-[99px] z-[1] block max-h-[60vh] w-full overflow-y-auto rounded-2xl p-0 shadow-sm',
        className
      )}
      ref={containerRef}
      id="product-section"
    >
      {(items || []).map((item) => {
        const isSelected = item.id === selectedItemId
        const NavItemContent = (
          <div
            className={cn(
              'cursor-pointer border-l-4 px-5 py-2.5 transition-colors',
              isSelected ? 'border-button text-button' : 'border-transparent',
              'hover:text-primary focus:text-primary'
            )}
            ref={(e) => {
              if (e) refs.set(item.id, { current: e })
            }}
          >
            <P className={cn('font-bold', isSelected && 'text-primary')}>
              {item.name}
            </P>
          </div>
        )

        return (
          <a
            key={item.id}
            href={item.href}
            onClick={(e) => onClickItem(e, item.id as string)}
          >
            {NavItemContent}
          </a>
        )
      })}
    </Card>
  )
}

export default VerticalNavigation
